import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

/// Actions
import { completeOnBoardingUser, getUserData } from '~/redux/actions/auth-actions';

// Utils
import { INDUSTRY_OPTIONS, DIRECT_MAIL_OPTIONS, ANNUAL_REVENUE_OPTIONS } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';

// MUI Components
import { Box, Input, InputLabel, Typography, CircularProgress } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { styled } from '@mui/material/styles';

import OlcButton from '~/components/General/OlcButton';

// Styles
import '../styles.scss';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(0),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const BestFit = (props) => {
  const { isOpen } = props;
  const [loading, setLoading] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState({
    id: '',
    title: '',
  });
  const [customIndustry, setCustomIndustry] = useState('');
  const [selectedDirectMail, setSelectedDirectMail] = useState({
    id: '',
    title: '',
  });
  const [selectedAnnualRevenue, setSelectedAnnualRevenue] = useState({
    id: '',
    title: '',
  });
  const [industryOptions, setIndustryOptions] = useState([...INDUSTRY_OPTIONS]);

  const dispatch = useDispatch();

  const completeOnBoarding = async (event) => {
    event.preventDefault();

    // Validate that all required fields are selected
    if (!selectedIndustry.id || !selectedDirectMail.id || !selectedAnnualRevenue.id) {
      // Handle validation error - could show toast or error message
      return;
    }

    setLoading(true);

    // Here you would typically send the data to your API
    const payload = {
      what_industry_are_you_part_of: selectedIndustry.title,
      are_you_currently_sending_direct_mail: selectedDirectMail.title,
      what_is_your_monthly_marketing_budget: selectedAnnualRevenue.title,
    };

    try {
      // Simulate API call
      const request = await dispatch(completeOnBoardingUser(payload));
      await dispatch(getUserData());
    } catch (error) {
      console.error('Error submitting best fit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIndustrySelect = (id, title) => {
    if (title === 'Other') {
      setCustomIndustry('');
      setSelectedIndustry({ id: id, title: title });
    } else {
      setSelectedIndustry({ id: id, title: title });
    }
  };

  const handleCustomIndustryChange = (e) => {
    setCustomIndustry(e.target.value);
  };

  const handleCustomIndustryKeyDown = (e) => {
    if (e.key === 'Enter' && customIndustry.trim() !== '') {
      // Find the index of "Other" option
      const otherIndex = industryOptions.findIndex((opt) => opt.title === 'Other');
      // Generate a unique ID by finding the maximum ID and incrementing
      const maxId = Math.max(...industryOptions.map((opt) => opt.id));
      const newId = maxId + 1;
      const newOption = {
        id: newId,
        title: customIndustry.trim(),
      };

      const newOptions = [...industryOptions];
      if (otherIndex !== -1) {
        // Insert new option before "Other"
        newOptions.splice(otherIndex, 0, newOption);
      } else {
        // If "Other" not found, add to the end
        newOptions.push(newOption);
      }

      setIndustryOptions(newOptions);

      // Select the newly added option
      setSelectedIndustry({
        id: newId,
        title: customIndustry.trim(),
      });
      setCustomIndustry('');
    }
  };

  return (
    <BootstrapDialog
      aria-labelledby="customized-dialog-title"
      open={isOpen}
      className="bestFitOnboardingModal"
    >
      <DialogContent className="bestFitDialogContent">
        <Box className="newCreateAccountWrapper bestFit">
          <Box className="createAccountFormWrapper maxWidth">
            <form onSubmit={completeOnBoarding} className="bestFitForm">
              <Box className="createAccountFormHeader bestFitModalHeader">
                <Typography variant="h4">{MESSAGES.SIGN_UP.NEW_TEXT.BEST_FIT_TEXT}</Typography>
                <Typography>{MESSAGES.SIGN_UP.NEW_TEXT.BEST_FIT_DESCRIPTION}</Typography>
              </Box>
              <Box className="bestFitModalBody">
                <Box className="newInputWrapper">
                  <InputLabel className="formLabel">
                    {MESSAGES.SIGN_UP.NEW_TEXT.INDUSTRY_QUESTION}
                    <span style={{ position: 'absolute', top: '-3px' }}>*</span>
                  </InputLabel>
                  <Box className="optionsWrapper">
                    {industryOptions.map((option) => {
                      // Replace "Other" option with input when it's selected
                      if (option.title === 'Other' && selectedIndustry.id === option.id) {
                        return (
                          <Input
                            key={option.id}
                            type="text"
                            placeholder="Please specify your industry"
                            autoComplete="off"
                            name="customIndustry"
                            className="customIndustry"
                            value={customIndustry}
                            onChange={handleCustomIndustryChange}
                            onKeyDown={handleCustomIndustryKeyDown}
                          />
                        );
                      }
                      return (
                        <Typography
                          key={option.id}
                          className={selectedIndustry.id === option.id ? 'selectedOption' : ''}
                          onClick={() => handleIndustrySelect(option.id, option.title)}
                        >
                          {option.title}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>
                <Box className="newInputWrapper" sx={{ margin: '16px 0 !important' }}>
                  <InputLabel className="formLabel">
                    {MESSAGES.SIGN_UP.NEW_TEXT.DIRECT_MAIL}
                    <span style={{ position: 'absolute', top: '-3px' }}>*</span>
                  </InputLabel>
                  <Box className="optionsWrapper">
                    {DIRECT_MAIL_OPTIONS.map((option) => (
                      <Typography
                        key={option.id}
                        className={selectedDirectMail.id === option.id ? 'selectedOption' : ''}
                        onClick={() =>
                          setSelectedDirectMail({ id: option.id, title: option.title })
                        }
                      >
                        {option.title}
                      </Typography>
                    ))}
                  </Box>
                </Box>
                <Box className="newInputWrapper">
                  <InputLabel className="formLabel">
                    {MESSAGES.SIGN_UP.NEW_TEXT.ANNUAL_REVENUE}
                    <span style={{ position: 'absolute', top: '-3px' }}>*</span>
                  </InputLabel>
                  <Box className="optionsWrapper">
                    {ANNUAL_REVENUE_OPTIONS.map((option) => (
                      <Typography
                        key={option.id}
                        className={selectedAnnualRevenue.id === option.id ? 'selectedOption' : ''}
                        onClick={() =>
                          setSelectedAnnualRevenue({ id: option.id, title: option.title })
                        }
                      >
                        {option.title}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
              <Box className="bestFitModalFooter">
                <OlcButton
                  variant="primary"
                  type="submit"
                  className="submitButton bestFitSubmitButton"
                  disabled={
                    !selectedIndustry.id || !selectedDirectMail.id || !selectedAnnualRevenue.id
                  }
                >
                  {loading ? (
                    <CircularProgress
                      sx={{
                        color: 'white',
                        width: '25px !important',
                        height: '25px !important',
                      }}
                    />
                  ) : (
                    MESSAGES.SIGN_UP.NEW_TEXT.LETS_GO
                  )}
                </OlcButton>
              </Box>
            </form>
          </Box>
        </Box>
      </DialogContent>
    </BootstrapDialog>
  );
};

export default BestFit;
